"use client";

import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@papier/ui/components/ui/card";
import { Skeleton } from "@papier/ui/components/ui/skeleton";
import type { Organization } from "better-auth/plugins/organization";
import { ArrowRight, AddCircle as PlusCircle } from "@papier/ui/components/icons";
import { useCallback, useContext, useState } from "react";
import { AuthUIContext } from "../../lib/auth-ui-provider";
import { getLocalizedError } from "../../lib/utils";
import { OrganizationCellView } from "./organization-cell-view";
import { CreateOrganizationDialog } from "./create-organization-dialog";

export interface OrganizationSwitcherProps {
  onSetActive?: () => Promise<void>;
}

/**
 * Displays a list of organizations that can be switched between
 *
 * Renders a list interface where users can:
 * - See all available organizations
 * - Click to switch to a different organization
 * - Create a new organization via a Plus icon next to the "Organizations" header
 */
export function OrganizationSwitcher({ onSetActive }: OrganizationSwitcherProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const {
    authClient,
    hooks: { useListOrganizations },
    localization: contextLocalization,
    toast,
  } = useContext(AuthUIContext);

  const localization = contextLocalization;
  const { data: organizations, isPending } = useListOrganizations();


  const switchActiveOrganization = useCallback(
    async (orgId: Organization["id"]) => {
      try {
        await authClient.organization.setActive({ organizationId: orgId }, { throw: true });
        await onSetActive?.();
      } catch (error) {
        toast({
          variant: "error",
          message: getLocalizedError({ error, localization }),
        });
      }
    },
    [authClient, toast, localization, onSetActive],
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg md:text-xl">{localization.ORGANIZATIONS}</CardTitle>
              <CardDescription className="text-xs md:text-sm">
                {localization.ORGANIZATIONS_DESCRIPTION}
              </CardDescription>
            </div>
            <Button
              aria-label={localization.CREATE_ORGANIZATION}
              data-testid="create-org-btn"
              type="button"
              variant="ghost"
              onClick={() => setCreateDialogOpen(true)}
            >
              <PlusCircle />
            </Button>
          </div>
        </CardHeader>
        {(isPending || (organizations && organizations.length > 0)) && (
          <CardContent className="grid gap-4">
            {isPending && <Skeleton className="h-50 w-full" />}
            {organizations?.map((organization) => (
              <Card className="flex-row p-4" key={organization.id}>
                <OrganizationCellView localization={localization} organization={organization} />

                <div className="ms-auto">
                  <Button
                    type="button"
                    title="Select organization"
                    variant={"ghost"}
                    onClick={() => switchActiveOrganization(organization.id)}
                  >
                    <ArrowRight />
                  </Button>
                </div>
              </Card>
            ))}
          </CardContent>
        )}
      </Card>
      <CreateOrganizationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSetActive={onSetActive}
      />
    </>
  );
}
